import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, Spin, Tooltip, Row, Col, Empty } from "antd";
import CorridorMap from "./CorridorMap";
import WarehouseService from "../../../../../../services/WarehouseService";
import CorridorColorReferences from "./CorridorColorReferences";
import Corridor from "../../../../../../models/Corridor";
import { useLanguage } from "../../../../../../context/LanguageContext";
import Position from "../../../../../../models/Position";

interface CorridorCollapseProps {
  warehouseId: string;
  isMobile:boolean;
}

const WarehouseMap: React.FC<CorridorCollapseProps> = (props) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState<boolean>(true);
  const [corridors, setCorridors] = useState<Corridor[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedCorridorName, setSelectedCorridorName] = useState<string | null>(null);

  const groupedPositionsByCorridor = useMemo(() => {
    const grouped: Record<string, Position[]> = {};
    for (const position of positions) {
      const corridorName = position.position_label.substring(0, 2);
      if (!grouped[corridorName]) {
        grouped[corridorName] = [];
      }
      grouped[corridorName].push(position);
    }
    return grouped;
  }, [positions]);

  const getCorridorVisualState = useCallback((corridorName: string) => {
    const corridorPositions = groupedPositionsByCorridor[corridorName] || [];
    const totalCount = corridorPositions.length;

    if (totalCount === 0) {
      return {
        isUnexplored: true,
        strokeColor: "#fadb14",
      };
    }

    let unknownCount = 0;
    let emptyCount = 0;
    let fullCount = 0;

    for (const position of corridorPositions) {
      if (position.state === "unknown") {
        unknownCount += 1;
      }
      if (position.state === "empty") {
        emptyCount += 1;
      }
      if (position.state === "full") {
        fullCount += 1;
      }
    }

    const readingRatio = 1 - unknownCount / totalCount;
    const effectiveRatio = (emptyCount + fullCount) / totalCount;

    if (readingRatio < 0.1) {
      return {
        isUnexplored: true,
        strokeColor: "#fadb14",
      };
    }

    if (effectiveRatio < 0.5) {
      return {
        isUnexplored: false,
        strokeColor: "#f5222d",
      };
    }

    if (effectiveRatio < 0.75) {
      return {
        isUnexplored: false,
        strokeColor: "#fadb14",
      };
    }

    return {
      isUnexplored: false,
      strokeColor: "#52c41a",
    };
  }, [groupedPositionsByCorridor]);

  const selectedCorridor = useMemo(() => {
    if (!selectedCorridorName) {
      return null;
    }
    return corridors.find((corridor) => corridor.name === selectedCorridorName) || null;
  }, [corridors, selectedCorridorName]);

  const getRackSelectorLabel = useCallback((corridorName: string) => {
    const lettersGroup = corridorName.trim().match(/[A-Za-z]+/)?.[0] || "";
    if (lettersGroup.length > 0) {
      return lettersGroup.slice(0, 2).toUpperCase();
    }
    return corridorName.trim().slice(0, 2).toUpperCase();
  }, []);

  const updateContent = useCallback(() => {
    // setLoading(true);
    WarehouseService.getCorridors(props.warehouseId).then((corridors)=>{
      if(corridors===null){
        return
      }
      WarehouseService.getPositions(props.warehouseId).then((res) => {
        if (res === null) {
          return;
        }
        setCorridors(corridors)
        setPositions(res);
        setLoading(false);
      })
    });
  }, [props.warehouseId]);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 600000);
    return () => clearInterval(interval);
  }, [updateContent]);

  useEffect(() => {
    if (corridors.length === 0) {
      setSelectedCorridorName(null);
      return;
    }
    if (!selectedCorridorName || !corridors.some((corridor) => corridor.name === selectedCorridorName)) {
      setSelectedCorridorName(corridors[0].name);
    }
  }, [corridors, selectedCorridorName]);

  return (
    <Spin spinning={loading}>
      <CorridorColorReferences />
      {corridors.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
      ) : (
        <Row gutter={[12, 12]} align="top">
          <Col xs={24} lg={24}>
            <Card
              size="small"
              title={language === "es" ? "Selector de Racks" : "Rack Selector"}
              bodyStyle={{ padding: "14px 10px 10px" }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))",
                  gap: "6px",
                  marginTop: "8px",
                  alignContent: "start",
                  paddingRight: "4px",
                }}
              >
                {corridors.map((corridor) => {
                  const corridorVisualState = getCorridorVisualState(corridor.name);
                  const isSelected = corridor.name === selectedCorridorName;
                  return (
                    <Tooltip
                      key={corridor.name}
                      title={language === "es" ? `Rack ${corridor.name}` : `Corridor ${corridor.name}`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedCorridorName(corridor.name)}
                        style={{
                          border: isSelected ? "2px solid #1677ff" : "1px solid #d9d9d9",
                          borderRadius: "8px",
                          background: isSelected ? "#e6f4ff" : "#fff",
                          padding: "4px 6px",
                          textAlign: "center",
                          cursor: "pointer",
                          minHeight: "44px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: "14px", lineHeight: 1 }}>
                          {getRackSelectorLabel(corridor.name)}
                        </div>
                        <div style={{ minHeight: "8px", display: "flex", alignItems: "center" }}>
                          {corridorVisualState.isUnexplored ? (
                            <Badge color="#fadb14" />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "6px",
                                borderRadius: "999px",
                                backgroundColor: corridorVisualState.strokeColor,
                              }}
                            />
                          )}
                        </div>
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={24}>
            {selectedCorridor ? (
              <Card
                title={language === "es" ? `Rack ${selectedCorridor.name}` : `Corridor ${selectedCorridor.name}`}
                bodyStyle={{ padding: "12px" }}
              >
                <CorridorMap
                  isMobile={props.isMobile}
                  warehouseId={props.warehouseId}
                  corridorInfo={selectedCorridor}
                  corridorPositions={groupedPositionsByCorridor[selectedCorridor.name] || []}
                />
              </Card>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
            )}
          </Col>
        </Row>
      )}
    </Spin>
  );
};

export default WarehouseMap;
